import * as React from 'react';
import {
  Search,
  X,
  Check,
  Filter,
  MapPin,
  Briefcase,
  Building2,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Resource, UtilisationTier } from '@/types';
import {
  getUtilizationLabel as getUtilizationLabelFromSettings,
  DEFAULT_UTILIZATION_TIERS,
} from '@/utils/utilization-labels';
// DEFAULT_UTILIZATION_TIERS is used as the default value for utilisationTiers prop

// ─── Types ────────────────────────────────────────────────────────────────────

interface MultiResourceSelectProps {
  resources: Resource[];
  selectedResourceIds: string[];
  onSelect: (resourceIds: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  calculateUtilization?: (resourceId: string) => number;
  utilisationTiers?: UtilisationTier[];
  className?: string;
  maxVisibleAvatars?: number;
}

interface ResourceWithUtilization extends Resource {
  utilization?: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return String(name ?? '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function getUtilizationColor(utilization: number): string {
  if (utilization >= 90) return 'bg-accent-destructive-500';
  if (utilization >= 70) return 'bg-accent-warning-500';
  return 'bg-accent-success-500';
}

function getUtilizationBadgeVariant(
  utilization: number
): 'success' | 'warning' | 'destructive' {
  if (utilization >= 90) return 'destructive';
  if (utilization >= 70) return 'warning';
  return 'success';
}

// ─── Main component ───────────────────────────────────────────────────────────

export function MultiResourceSelect({
  resources,
  selectedResourceIds,
  onSelect,
  disabled = false,
  placeholder = 'Search resources...',
  calculateUtilization,
  utilisationTiers = DEFAULT_UTILIZATION_TIERS,
  className,
}: MultiResourceSelectProps) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = React.useState<string[]>([]);

  const searchRef = React.useRef<HTMLInputElement>(null);

  // ── Derived data ──────────────────────────────────────────────────────────
  const enriched: ResourceWithUtilization[] = React.useMemo(
    () =>
      resources.map((r) => ({
        ...r,
        utilization: calculateUtilization ? calculateUtilization(r.id) : undefined,
      })),
    [resources, calculateUtilization]
  );

  const uniqueRoles = React.useMemo(
    () => [...new Set(resources.map((r) => r.role).filter(Boolean))].sort(),
    [resources]
  );

  const uniqueDepartments = React.useMemo(
    () =>
      [
        ...new Set(
          resources.map((r) => r.department).filter((d): d is string => Boolean(d))
        ),
      ].sort(),
    [resources]
  );

  const activeFilterCount = selectedRoles.length + selectedDepartments.length;

  // ── Filter + search + sort ────────────────────────────────────────────────
  const filtered = React.useMemo(() => {
    let list = enriched;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.role.toLowerCase().includes(q) ||
          (r.department ?? '').toLowerCase().includes(q) ||
          (r.location ?? '').toLowerCase().includes(q)
      );
    }

    if (selectedRoles.length > 0) {
      list = list.filter((r) => selectedRoles.includes(r.role));
    }

    if (selectedDepartments.length > 0) {
      list = list.filter((r) => r.department && selectedDepartments.includes(r.department));
    }

    // Sort: selected first, then by utilization (low first = more available)
    return [...list].sort((a, b) => {
      const aSelected = selectedResourceIds.includes(a.id);
      const bSelected = selectedResourceIds.includes(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      if (a.utilization === undefined && b.utilization === undefined) return 0;
      if (a.utilization === undefined) return 1;
      if (b.utilization === undefined) return -1;
      return a.utilization - b.utilization;
    });
  }, [enriched, searchQuery, selectedRoles, selectedDepartments, selectedResourceIds]);

  const selectedResources = React.useMemo(
    () => resources.filter((r) => selectedResourceIds.includes(r.id)),
    [resources, selectedResourceIds]
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggle = (id: string) => {
    const isSelected = selectedResourceIds.includes(id);
    onSelect(
      isSelected
        ? selectedResourceIds.filter((x) => x !== id)
        : [...selectedResourceIds, id]
    );
  };

  const handleRemove = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(selectedResourceIds.filter((x) => x !== id));
  };

  const handleClearAll = () => onSelect([]);

  const handleSelectAll = () => {
    const allIds = filtered.map((r) => r.id);
    onSelect([...new Set([...selectedResourceIds, ...allIds])]);
  };

  const handleDeselectAll = () => {
    const filteredIds = new Set(filtered.map((r) => r.id));
    onSelect(selectedResourceIds.filter((id) => !filteredIds.has(id)));
  };

  const toggleRole = (role: string) =>
    setSelectedRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );

  const toggleDepartment = (dept: string) =>
    setSelectedDepartments((prev) =>
      prev.includes(dept) ? prev.filter((d) => d !== dept) : [...prev, dept]
    );

  const clearFilters = () => {
    setSelectedRoles([]);
    setSelectedDepartments([]);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className={cn(
        'flex flex-col rounded-lg border bg-card overflow-hidden',
        disabled && 'pointer-events-none opacity-50',
        className
      )}
    >
      {/* ── Search bar ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 px-3 border-b bg-background">
        <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <input
          ref={searchRef}
          type="text"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={disabled}
          className="flex-1 h-9 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery('')}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <Separator orientation="vertical" className="h-4 mx-1" />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={cn(
                'relative flex items-center justify-center h-7 w-7 rounded-md transition-colors',
                showFilters
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Filter className="h-3.5 w-3.5" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-primary text-[9px] font-bold text-primary-foreground flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">Filters</TooltipContent>
        </Tooltip>
      </div>

      {/* ── Filter panel ───────────────────────────────────────────────────── */}
      {showFilters && (
        <div className="border-b bg-muted/30 px-3 py-3 space-y-3">
          {/* Role chips */}
          {uniqueRoles.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Briefcase className="h-3 w-3" />
                Role
              </p>
              <div className="flex flex-wrap gap-1.5">
                {uniqueRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    onClick={() => toggleRole(role)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                      selectedRoles.includes(role)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    )}
                  >
                    {selectedRoles.includes(role) && <Check className="h-2.5 w-2.5" />}
                    {role}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Department chips */}
          {uniqueDepartments.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <Building2 className="h-3 w-3" />
                Department
              </p>
              <div className="flex flex-wrap gap-1.5">
                {uniqueDepartments.map((dept) => (
                  <button
                    key={dept}
                    type="button"
                    onClick={() => toggleDepartment(dept)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                      selectedDepartments.includes(dept)
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    )}
                  >
                    {selectedDepartments.includes(dept) && <Check className="h-2.5 w-2.5" />}
                    {dept}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Clear filters */}
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* ── Meta bar: count + bulk actions ─────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/20 shrink-0">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{filtered.length}</span>{' '}
          resource{filtered.length !== 1 ? 's' : ''}
          {selectedResourceIds.length > 0 && (
            <>
              {' '}
              &bull;{' '}
              <span className="font-medium text-foreground">{selectedResourceIds.length}</span> selected
            </>
          )}
        </p>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={filtered.length === 0}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none transition-colors font-medium"
          >
            Select all
          </button>
          <span className="text-muted-foreground text-xs mx-0.5">·</span>
          <button
            type="button"
            onClick={handleDeselectAll}
            disabled={selectedResourceIds.length === 0}
            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40 disabled:pointer-events-none transition-colors font-medium"
          >
            Deselect all
          </button>
        </div>
      </div>

      {/* ── Resource list ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto" style={{ maxHeight: '300px' }}>
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-muted-foreground">
            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
              <Users className="h-4 w-4" />
            </div>
            <p className="text-sm font-medium">No resources found</p>
            <p className="text-xs">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div>
            {filtered.map((resource) => {
              const isSelected = selectedResourceIds.includes(resource.id);
              const util = resource.utilization;

              return (
                <button
                  key={resource.id}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleToggle(resource.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-border/50 last:border-0',
                    isSelected
                      ? 'bg-primary/[0.05] hover:bg-primary/[0.08]'
                      : 'hover:bg-muted/40'
                  )}
                >
                  {/* Checkbox */}
                  <div
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border-[1.5px] transition-colors',
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground/40 bg-background'
                    )}
                  >
                    {isSelected && <Check className="h-2.5 w-2.5" />}
                  </div>

                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={resource.photoUrl} alt={resource.name} />
                      <AvatarFallback className="text-xs">
                        {getInitials(resource.name)}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{resource.name}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground truncate">{resource.role}</span>
                      {resource.department && (
                        <>
                          <span className="text-muted-foreground/40 text-xs">·</span>
                          <span className="text-xs text-muted-foreground truncate">
                            {resource.department}
                          </span>
                        </>
                      )}
                    </div>

                    {/* Utilisation bar */}
                    {util !== undefined && (
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1 bg-muted rounded-full overflow-hidden max-w-[80px]">
                          <div
                            className={cn('h-full rounded-full transition-all', getUtilizationColor(util))}
                            style={{ width: `${Math.min(util, 100)}%` }}
                          />
                        </div>
                        <Badge
                          variant={getUtilizationBadgeVariant(util)}
                          className="text-[10px] px-1.5 py-0 h-4 shrink-0"
                        >
                          {getUtilizationLabelFromSettings(util, utilisationTiers)}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  {resource.location && (
                    <div className="flex items-center gap-1 shrink-0 text-muted-foreground">
                      <MapPin className="h-3 w-3" />
                      <span className="text-[11px] max-w-[80px] truncate">{resource.location}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Selected resource chips ─────────────────────────────────────────── */}
      {selectedResources.length > 0 && (
        <div className="border-t bg-muted/20 px-3 py-2.5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Selected ({selectedResources.length})
            </p>
            <button
              type="button"
              onClick={handleClearAll}
              className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {selectedResources.map((r) => (
              <div
                key={r.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/[0.06] pl-0.5 pr-2 py-0.5"
              >
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={r.photoUrl} alt={r.name} />
                  <AvatarFallback className="text-[8px]">
                    {getInitials(r.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs font-medium text-foreground max-w-[100px] truncate">
                  {r.name}
                </span>
                <button
                  type="button"
                  onClick={(e) => handleRemove(r.id, e)}
                  className="text-muted-foreground hover:text-foreground transition-colors ml-0.5"
                  aria-label={`Remove ${r.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CompactMultiResourceSelect ───────────────────────────────────────────────
// Kept for backward-compat — delegates to main component

export function CompactMultiResourceSelect(props: MultiResourceSelectProps) {
  return <MultiResourceSelect {...props} />;
}
